import { pageUrls } from '~constants/pageUrls';
import type { Accommodation, RoomRequest } from '~types';
import { GeoType } from '~types';
import { SortingEnum } from '~types/SortingEnum';

interface ResultsPageParams {
  checkInDate: string;
  checkOutDate: string;
  geoId: number;
  buyPath: string;
  roomRequests?: RoomRequest[];
  sort?: SortingEnum;
  dedupId?: Accommodation['dedupId'];
  isSearchExtension?: boolean;
  isMapOpened?: boolean;
  dealsId?: string;
  geoType?: GeoType;
}

interface UrlCreationOptions {
  shouldKeepQueryParams?: boolean;
  shouldUseGeoNodeId?: boolean;
}

const CHILDREN_SEPARATOR = '-';

const DEFAULT_GUESTS_NUMBER = 2;

export const getResultsPageUrl: (
  params: ResultsPageParams,
  options?: UrlCreationOptions
) => URL = (
  {
    checkInDate,
    checkOutDate,
    geoId,
    buyPath,
    roomRequests = [
      {
        numAdults: DEFAULT_GUESTS_NUMBER,
      },
    ],
    sort,
    dedupId,
    isSearchExtension,
    isMapOpened,
    dealsId,
    geoType = GeoType.Accommodation,
  },
  options = {}
) => {
  const accommodationFunnelUrl: URL = new URL(
    pageUrls.accommodationFunnel,
    window.location.origin
  );

  let urlSearchParams: string = '';

  if (options.shouldKeepQueryParams) {
    urlSearchParams = new URL(window.location.href).searchParams.toString();
  }

  const [{ childrenAges }] = roomRequests;

  const paramsMap: Map<string, string> = new Map([
    ['checkIn', checkInDate],
    ['checkOut', checkOutDate],
    ['room0Adults', roomRequests[0].numAdults.toString()],
    ['buyPath', buyPath],
  ]);

  const querystringParams: URLSearchParams = new URLSearchParams(
    urlSearchParams
  );

  if (options.shouldUseGeoNodeId) {
    paramsMap.set('geoNodeId', geoId.toString());
  } else {
    paramsMap.set('geoId', `${geoId}%2B${geoType}`);
  }

  if (Array.isArray(childrenAges) && childrenAges.length > 0) {
    paramsMap.set('room0ChildrenAges', childrenAges.join(CHILDREN_SEPARATOR));
  }

  // Move sort parameter to query string for MFE format
  if (sort && SortingEnum[sort]) {
    querystringParams.append('sort', SortingEnum[sort]);
  }

  // Move accommodation parameter to query string for MFE format
  if (dedupId) {
    querystringParams.append('accommodation', `${dedupId}+SearchByProperty`);
  }

  if (isSearchExtension) {
    paramsMap.set('searchExtension', Boolean(isSearchExtension).toString());
  }

  if (isMapOpened) {
    paramsMap.set('resultsMapView', 'opened');
    querystringParams.append('resultsMapView', 'opened');
  }

  if (dealsId) {
    paramsMap.set('dealsId', dealsId);
  }

  // Build path parameters for MFE format (no hash)
  const pathParams: string = Array.from(paramsMap)
    .map(([key, value]) => `${key}=${value}`)
    .join(';');

  // Update pathname for MFE format: /travel/accommodation/hotel_results/params
  accommodationFunnelUrl.pathname = `${pageUrls.accommodationFunnel}${pageUrls.resultsPage}/${pathParams}`;
  accommodationFunnelUrl.search = querystringParams.toString();

  return accommodationFunnelUrl;
};

type SortParams =
  | `sort=${SortingEnum.DISTANCE}`
  | `sort=${SortingEnum.PRICE}`
  | `sort=${SortingEnum.BEST_DEALS}`
  | '';

export const getUrlSortParams = (sort: SortingEnum): SortParams => {
  let sortParam: SortingEnum | '';

  switch (sort) {
    case SortingEnum.DISTANCE:
      sortParam = SortingEnum.DISTANCE;
      break;
    case SortingEnum.PRICE:
      sortParam = SortingEnum.PRICE;
      break;
    case SortingEnum.BEST_DEALS:
      sortParam = SortingEnum.BEST_DEALS;
      break;
    default:
      sortParam = '';
  }

  return sortParam ? `sort=${sortParam}` : '';
};
